import { makeStyles } from '@mui/styles';
import Accordion from 'apollo-react/components/Accordion';
import AccordionDetails from 'apollo-react/components/AccordionDetails';
import AccordionSummary from 'apollo-react/components/AccordionSummary';
import Button from 'apollo-react/components/Button';
import FormHelperText from 'apollo-react/components/FormHelperText/FormHelperText';
import Grid from 'apollo-react/components/Grid';
import Loader from 'apollo-react/components/Loader';
import Typography from 'apollo-react/components/Typography';
import ApolloFileViewer from 'apollo-react-file-viewer';
import { getType } from 'apollo-react-file-viewer/FileViewer';
import mimeTypes from 'mime-types';
import qs from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import AttachmentDescription from './AttachmentDescription';
import { DownloadFileLink } from './DownloadFileLink';
import { RequestPrintButton } from './RequestPrintButton';
import ScanTextButton from './ScanTextButton';
import { viewDocument } from '../../actions/DocumentViewerActions';
import { fetchAttachmentFromServer } from '../../api/attachments';
import { CERTIFICATION_PAGE, DOCUMENT_COMPARE, DOCUMENT_VIEWER } from '../../constants/Urls';
import { getCurrentAttachmentText, getIsTextLoading } from '../../selectors/AttachmentsSelectors';
import { getUITA } from '../../utils/commonUtils';
import { isNilOrEmpty } from '../../utils/ramdaUtils';
import withErrorBoundary from '../withErrorBoundary';
import withTranslation from '../withTranslation';

const useStyles = makeStyles({
  container: {
    height: '100%',
    position: 'relative',
  },
  message: {
    display: 'flex !important',
    flexDirection: 'column',
    alignItems: 'center',
  },

  hyperlinkExtraControl: {
    marginRight: 12,
  },
  hyperlinkFormatUnsupported: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 14,
    marginTop: 10,
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 14,
    marginTop: 10,
  },
  extraControls: {
    display: 'flex',
    '& > *': {
      marginRight: 12,
    },
  },
  toggleThumbnails: {
    marginRight: 12,
  },
  iframePdfViewer: {
    borderBottom: '1px solid #ddd',
    borderTop: '1px solid #ddd',
    borderLeft: 'none',
    borderRight: 'none',
  },
  panelGroup: {
    display: 'flex',
    padding: 0,
    overflow: 'hidden',
    width: 1200,
  },
  wrapper: {
    display: 'flex',
  },
  fileViewerWrapper: {
    flex: 1,
  },
  description: {
    flex: '0 0 25%',
  },
  label: {
    fontSize: 14,
  },
});

const MIN_HEIGHT = 500;

const supportedDoctypes = [
  'docx',
  'pdf',
  'jpg',
  'jpeg',
  'webp',
  'gif',
  'png',
  'bmp',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/png',
  'image/bmp',
  'xlsx',
  'xlsm',
  'xlsb',
  'xls',
  'csv',
  'prn',
  'ods',
  'text/csv',
  'application/x-prn',
  'application/vnd.ms-excel',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.ms-excel.sheet.macroenabled.12',
  'application/vnd.ms-excel.sheet.binary.macroenabled.12',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'mp4',
  'webm',
  'ogm',
  'ogv',
  'ogg',
  'video/mp4',
  'video/webm',
  'video/ogg',
];

const FileViewer = ({
  label = '',
  filename = '',
  height,
  params,
  getFileContents,
  translateString,
  hasRenderError,
  isDocCompare = false,
  isAttachment = false,
}) => {
  const classes = useStyles();

  const [type, setType] = useState();
  const [error, setError] = useState();
  const [errorNumber, setErrorNumber] = useState();
  const [filePath, setFilePath] = useState();
  const [extension, setExtension] = useState();
  const [hideThumbnails, setHideThumbnails] = useState(true);
  const [viewerHeight, setViewerHeight] = useState();
  const [unsupportedFormatMessage, setUnsupportedFormatMessage] = useState();
  const [failedToLoadMessage, setFailedToLoadMessage] = useState();
  const [isGroupingAborted, setIsGroupingAborted] = useState();
  const isTextLoading = useSelector(getIsTextLoading);
  const attachmentText = useSelector((state) =>
    getCurrentAttachmentText(state, { attachmentId: params?.ItmId })
  );
  const queryString = qs.stringify({ filename, ...params });
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  // If `height` is not specified, calculate it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const containerRef = useCallback((node) => {
    if (node !== null) {
      if (height) {
        setViewerHeight(height);
      } else {
        const rectHeight = node.getBoundingClientRect().height;
        setViewerHeight(rectHeight > MIN_HEIGHT ? rectHeight : MIN_HEIGHT);
      }
    }
  });

  const labelCapitalFirstLetter = (label) => {
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  useEffect(() => {
    // allows to access 'dispatch' from pdfjs iframe
    window.reduxDispatch = dispatch;

    return () => {
      delete window.reduxDispatch;
    };
  }, [dispatch]);

  useEffect(() => {
    const getUnsupportedFormatMessage = async () => {
      const resUnsupportedFormat = translateString(
        'File format is not supported, download file to view.'
      );
      setUnsupportedFormatMessage(resUnsupportedFormat);

      const resFailedToLoad = translateString('Failed to load file contents.');
      setFailedToLoadMessage(resFailedToLoad);
    };
    getUnsupportedFormatMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (![DOCUMENT_COMPARE, DOCUMENT_VIEWER, CERTIFICATION_PAGE].includes(pathname)) {
      const { contentType, parId, entityKey } = params;
      dispatch(viewDocument({ docId: parId, contentType, entityName: entityKey }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const fetchFileContents = getFileContents || fetchAttachmentFromServer;

  useEffect(() => {
    fetchFileContents(params)
      .then(({ data, headers }) => {
        const contentType = headers['content-type'];
        const contentDisposition = headers['content-disposition'];
        // if content-type is json, it's an error response
        if (contentType === 'application/json') {
          const reader = new FileReader();
          reader.addEventListener('loadend', () => {
            const { errors = [] } = JSON.parse(reader.result);
            setError(errors.map((e) => e.errorMessage).join(' '));
            setErrorNumber(errors[0].errorNumber);
          });
          reader.readAsText(data);
        }

        let responseFilename = '';

        if (contentDisposition) {
          [, responseFilename] = contentDisposition.match(/filename="(.+?)"/);
        }

        const nameParts = (responseFilename || filename).split('.');
        const extension =
          nameParts.length > 1 ? nameParts.pop().toLowerCase() : mimeTypes.extension(contentType);

        setFilePath(URL.createObjectURL(data));
        setExtension(extension);
        !isNilOrEmpty(responseFilename) && setType(getType(`${filePath}${responseFilename}`));
      })
      .catch((error) => {
        const unsupportedExtension = error.message.split(' ')[0] || '';
        setType(unsupportedExtension);
      });

    return () => URL.revokeObjectURL(filePath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderDocumentButtons = () => {
    if ([DOCUMENT_VIEWER, CERTIFICATION_PAGE].includes(pathname)) {
      const container = document.getElementById('file-viewer-buttons-container');

      return container
        ? createPortal(
            <>
              {isAttachment && (
                <ScanTextButton
                  attachmentId={params?.ItmId}
                  disabled={attachmentText}
                  extension={extension}
                />
              )}
              <RequestPrintButton />
              <DownloadFileLink
                queryString={queryString}
                fetchFileContents={fetchFileContents}
                isDocCompare={isDocCompare}
                isAttachment={isAttachment}
              />
            </>,
            container
          )
        : null;
    }

    return (
      <div className={classes.buttonsContainer}>
        {isAttachment && (
          <ScanTextButton
            attachmentId={params?.ItmId}
            disabled={attachmentText}
            extension={extension}
          />
        )}
        <RequestPrintButton />
        <DownloadFileLink
          queryString={queryString}
          fetchFileContents={fetchFileContents}
          isDocCompare={isDocCompare}
          isAttachment={isAttachment}
        />
      </div>
    );
  };

  if (error) {
    // error 50200 is related to scurity access, in that case, users wont be able to download the file
    if (errorNumber === '50200') {
      return (
        <FormHelperText error>
          <Typography variant="title2" gutterBottom>
            {failedToLoadMessage}
          </Typography>
          <Typography variant="error">{error}</Typography>
        </FormHelperText>
      );
    } else {
      const errorComponent = (
        <>
          {renderDocumentButtons()}
          <FormHelperText error>
            <Typography variant="title2" gutterBottom>
              {failedToLoadMessage}
            </Typography>
            <Typography variant="error">{error}</Typography>
          </FormHelperText>
        </>
      );

      return label ? (
        <Accordion defaultExpanded>
          <AccordionSummary>
            <Typography className={classes.label}>
              {translateString(labelCapitalFirstLetter(label))}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>{errorComponent}</AccordionDetails>
        </Accordion>
      ) : (
        errorComponent
      );
    }
  }

  if (!filePath) {
    return <Loader />;
  }

  const isFormatSupported = supportedDoctypes.includes(extension);
  const errorMessage = hasRenderError
    ? translateString('Unable to display the content. Download the file to continue.')
    : unsupportedFormatMessage;

  const component =
    !hasRenderError && isFormatSupported && type ? (
      <>
        {renderDocumentButtons()}
        <div className={classes.wrapper}>
          <div className={classes.fileViewerWrapper}>
            <span ui-ta={getUITA('FileViewer')}>
              {type === 'pdf' ? (
                <iframe
                  className={classes.iframePdfViewer}
                  id="pdf-js-viewer"
                  src={`libs/pdfjs/web/viewer.html?file=${filePath}`}
                  title="webviewer"
                  width="100%"
                  height={`${viewerHeight}px`}
                />
              ) : (
                <ApolloFileViewer
                  src={filePath}
                  type={type}
                  height={viewerHeight}
                  hideThumbnails={hideThumbnails}
                  extraControls={
                    <div className={classes.extraControls}>
                      {isGroupingAborted ? null : (
                        <Button
                          variant="secondary"
                          onClick={() => setHideThumbnails(!hideThumbnails)}
                        >
                          {hideThumbnails
                            ? translateString('Show Thumbnails')
                            : translateString('Hide Thumbnails')}
                        </Button>
                      )}
                    </div>
                  }
                  showControls="click-hide"
                  onGroupingAborted={() => setIsGroupingAborted(true)}
                  thumbsProps={{ 'ui-ta': 'FileViewer-thumbs' }}
                  viewerProps={{ 'ui-ta': 'FileViewer-document ScrollingElement' }}
                />
              )}
            </span>
          </div>
          {attachmentText && (
            <div className={classes.description}>
              <AttachmentDescription description={attachmentText} />
            </div>
          )}
        </div>
        {isTextLoading && <Loader isInner />}
      </>
    ) : (
      <>
        {renderDocumentButtons()}
        <Grid container>
          <Grid item xs={12} className={classes.message}>
            <Typography variant="title2" gutterBottom>
              {errorMessage}
            </Typography>
          </Grid>
        </Grid>
      </>
    );

  return (
    <div ref={containerRef} className={classes.container}>
      {label ? (
        <Accordion defaultExpanded>
          <AccordionSummary>
            <Typography className={classes.label}>
              {translateString(labelCapitalFirstLetter(label))}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>{component}</AccordionDetails>
        </Accordion>
      ) : (
        component
      )}
    </div>
  );
};

export default withErrorBoundary(withTranslation(FileViewer));
